import type { Component, JSX } from 'solid-js'
import { For } from 'solid-js'

// =============================================================================
// Primitives
// =============================================================================

/** Pulse animation wrapper for skeleton content */
export const Skeleton: Component<{
  class?: string
  children?: JSX.Element
}> = (props) => <div class={`animate-pulse ${props.class ?? ''}`}>{props.children}</div>

/** Rounded bar shape for text and label placeholders */
export const SkeletonLine: Component<{
  class?: string
}> = (props) => <div class={`rounded bg-gray-200 ${props.class ?? ''}`} />

/** Circle shape for avatars, icons, and indicator dots */
export const SkeletonCircle: Component<{
  class?: string
}> = (props) => <div class={`rounded-full bg-gray-200 ${props.class ?? ''}`} />

/** Rectangle shape for images, chart areas, and content blocks */
export const SkeletonRect: Component<{
  class?: string
}> = (props) => <div class={`rounded-lg bg-gray-200 ${props.class ?? ''}`} />

// =============================================================================
// Presets
// =============================================================================

/** Simple summary card with two centered text lines */
export const SkeletonCard: Component<{
  class?: string
}> = (props) => (
  <Skeleton
    class={`p-2.5 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col justify-center items-center gap-1 md:flex-1 md:h-20 md:p-4 md:gap-2 ${
      props.class ?? ''
    }`}
  >
    <SkeletonLine class="h-5 w-16 md:h-7 md:w-20" />
    <SkeletonLine class="h-3 w-20 md:w-24" />
  </Skeleton>
)

/** Metric card with icon block, title, and value */
export const SkeletonMetricCard: Component<{
  class?: string
  variant?: 'card' | 'none'
}> = (props) => (
  <Skeleton
    class={
      props.variant === 'none'
        ? props.class ?? ''
        : `rounded-lg border border-gray-200 bg-white p-6 shadow-sm flex-1 ${props.class ?? ''}`
    }
  >
    <div class="flex items-center gap-4">
      <SkeletonRect class="h-12 w-12" />
      <div class="flex flex-col gap-2 flex-1">
        <SkeletonLine class="h-3.5 w-24" />
        <SkeletonLine class="h-7 w-32" />
      </div>
    </div>
  </Skeleton>
)

/** Line chart with legend dots and chart area */
export const SkeletonChart: Component<{
  class?: string
  legendItems?: number
}> = (props) => (
  <Skeleton class={`rounded-lg border border-gray-200 bg-white p-6 shadow-sm flex flex-col gap-4 ${props.class ?? ''}`}>
    <div class="flex flex-wrap items-center justify-center gap-6">
      <For each={Array.from({ length: props.legendItems ?? 3 })}>
        {() => (
          <div class="flex items-center gap-2">
            <SkeletonCircle class="h-3 w-3" />
            <SkeletonLine class="h-3 w-16" />
          </div>
        )}
      </For>
    </div>
    <SkeletonRect class="h-64 w-full" />
  </Skeleton>
)

/** Donut chart with ring and legend list */
export const SkeletonDonutChart: Component<{
  class?: string
  legendItems?: number
}> = (props) => (
  <Skeleton
    class={`flex h-full min-h-[300px] flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm ${
      props.class ?? ''
    }`}
  >
    <div class="flex items-center gap-6">
      <div class="flex flex-[1.5] items-center justify-center">
        <div class="h-64 w-64 rounded-full border-[48px] border-gray-200 bg-transparent" />
      </div>
      <div class="flex flex-1 flex-col gap-3">
        <For each={Array.from({ length: props.legendItems ?? 4 })}>
          {() => (
            <div class="flex items-center gap-2">
              <SkeletonCircle class="h-2.5 w-2.5" />
              <SkeletonLine class="h-3 w-20" />
              <SkeletonLine class="h-4 w-10 ml-auto" />
            </div>
          )}
        </For>
      </div>
    </div>
  </Skeleton>
)

/** Media list with title and items (thumbnails + text) */
export const SkeletonList: Component<{
  class?: string
  items?: number
}> = (props) => (
  <Skeleton class={props.class ?? ''}>
    <SkeletonLine class="h-4 w-32 mb-3" />
    <div class="flex flex-col [&>div]:py-3 [&>div]:border-b [&>div]:border-gray-100 [&>div:last-child]:border-0">
      <For each={Array.from({ length: props.items ?? 5 })}>
        {() => (
          <div class="flex items-center gap-3">
            <SkeletonLine class="h-4 w-4" />
            <SkeletonRect class="h-12 w-12" />
            <SkeletonLine class="h-3.5 flex-1 max-w-28" />
            <SkeletonLine class="h-4 w-12 ml-auto" />
          </div>
        )}
      </For>
    </div>
  </Skeleton>
)

// =============================================================================
// New Presets for Page Migration
// =============================================================================

/** Matches SummaryCard layout: centered value + change badge + label */
export const SkeletonSummaryCard: Component<{
  class?: string
}> = (props) => (
  <Skeleton class={`flex-1 rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm ${props.class ?? ''}`}>
    <div class="flex items-center justify-center gap-3">
      <SkeletonLine class="h-6 w-16" />
      <SkeletonLine class="h-4 w-14" />
    </div>
    <SkeletonLine class="mx-auto mt-2 h-3 w-24" />
  </Skeleton>
)

export const SkeletonTableRows: Component<{
  class?: string
  columns?: number
  rows?: number
}> = (props) => (
  <For each={Array.from({ length: props.rows ?? 5 })}>
    {() => (
      <tr class="border-b border-gray-100 last:border-0">
        <For each={Array.from({ length: props.columns ?? 5 })}>
          {() => (
            <td class="px-4 py-3">
              <SkeletonLine class="h-4 w-32" />
            </td>
          )}
        </For>
      </tr>
    )}
  </For>
)

/** Generic table skeleton: header row + N placeholder rows with configurable columns */
export const SkeletonTable: Component<{
  class?: string
  columns?: number
  rows?: number
}> = (props) => {
  const cols = () => props.columns ?? 5
  const rowCount = () => props.rows ?? 5

  const colWidths = ['w-32', 'w-24', 'w-28', 'w-20', 'w-24', 'w-20', 'w-28']

  return (
    <Skeleton class={`overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm ${props.class ?? ''}`}>
      <table class="w-full">
        <thead>
          <tr class="border-b border-gray-200">
            <For each={Array.from({ length: cols() })}>
              {(_, i) => (
                <th class="px-4 py-3 text-left">
                  <SkeletonLine class={`h-3 ${colWidths[i() % colWidths.length]}`} />
                </th>
              )}
            </For>
          </tr>
        </thead>
        <tbody>
          <SkeletonTableRows columns={cols()} rows={rowCount()} />
        </tbody>
      </table>
    </Skeleton>
  )
}

/** Engagement card skeleton for TopEngagementCard sections */
export const SkeletonEngagementCard: Component<{
  class?: string
}> = (props) => (
  <Skeleton
    class={`rounded-lg border border-gray-200 bg-white p-5 shadow-sm flex-none min-w-[400px] 2xl:flex-1 2xl:min-w-0 ${
      props.class ?? ''
    }`}
  >
    <SkeletonLine class="h-4 w-36 mb-4" />
    <div class="flex flex-col gap-3">
      <For each={Array.from({ length: 5 })}>
        {() => (
          <div class="flex items-center gap-3">
            <SkeletonCircle class="h-8 w-8" />
            <SkeletonLine class="h-3.5 flex-1 max-w-32" />
            <SkeletonLine class="h-4 w-12 ml-auto" />
          </div>
        )}
      </For>
    </div>
    <SkeletonLine class="mx-auto mt-4 h-3 w-16" />
  </Skeleton>
)
